// ADR-036 "Knowledge Library" cluster: the tenant Library page (src/pages/knowledge-library/), the bank page's
// "From the Library" tab, and the small Library-mode additions to the reused Discovery / Sources tabs. Spread
// into the central LABELS in labels.ts.
//
// The portal's `t()` takes no variables, so `{name}`, `{n}`, `{bank}` etc. are substituted by the caller with
// .replace(). Counted sentences have separate None / One / Many keys: Hebrew plural agreement cannot be done by
// string substitution.
//
// UI vocabulary: a Library "source" is shown as an "article"; a "link" is shown as "added to this bank".
export const KNOWLEDGE_LIBRARY_LABELS = {
  // ── Nav + page shell ──────────────────────────────────────────────────
  navKnowledgeLibrary: { en: 'Knowledge Library', he: 'ספריית ידע' },
  klTitle: { en: 'Knowledge Library', he: 'ספריית ידע' },
  klIntro: {
    en: 'Content here is indexed once. Each knowledge bank picks the articles it needs from the Library.',
    he: 'התוכן כאן עובר אינדוקס פעם אחת. כל מאגר ידע בוחר מהספרייה את המאמרים שהוא צריך.',
  },
  klChecking: { en: 'Checking the Knowledge Library…', he: 'בודק את ספריית הידע…' },
  klNotEnabled: {
    en: 'The Knowledge Library is not switched on for this organization.',
    he: 'ספריית הידע אינה מופעלת עבור הארגון הזה.',
  },
  klBankNotReady: {
    en: 'The Library is switched on but its storage has not been created yet. Reload the page in a moment.',
    he: 'הספרייה מופעלת אך האחסון שלה עדיין לא נוצר. טענו את הדף מחדש בעוד רגע.',
  },
  klBankLoadError: { en: 'Could not load the Library.', he: 'לא ניתן היה לטעון את הספרייה.' },
  klBackToBanks: { en: '← Knowledge Banks', he: '→ מאגרי ידע' },

  // ── Tabs ───────────────────────────────────────────────────────────────
  klTabArticles: { en: 'Articles', he: 'מאמרים' },
  klTabDiscover: { en: 'Discover', he: 'גילוי' },
  klTabUpload: { en: 'Upload', he: 'העלאה' },
  klTabMedia: { en: 'Media', he: 'מדיה' },

  // ── Articles table ─────────────────────────────────────────────────────
  klSearchPlaceholder: { en: 'Search by name or URL', he: 'חיפוש לפי שם או כתובת' },
  klSearchButton: { en: 'Search', he: 'חיפוש' },
  klColName: { en: 'Article', he: 'מאמר' },
  klColOwner: { en: 'Owner', he: 'בעלים' },
  klColUsedBy: { en: 'Used by', he: 'בשימוש אצל' },
  klColEntries: { en: 'Entries', he: 'רשומות' },
  klColStatus: { en: 'Status', he: 'סטטוס' },
  klColAdded: { en: 'Added', he: 'נוסף' },
  klColActions: { en: 'Actions', he: 'פעולות' },
  klOwnerLibrary: { en: 'Library', he: 'ספרייה' },
  klUsedByNone: { en: 'No bank uses this yet', he: 'אף מאגר עדיין לא משתמש בזה' },
  klUsedByOwnerOnly: { en: 'Only its own bank', he: 'רק המאגר שלו' },
  klChipOff: { en: 'off', he: 'כבוי' },
  klChipOffTitle: { en: 'Added to this bank but turned off there', he: 'נוסף למאגר הזה אך כבוי בו' },
  klChipOnTitle: { en: 'Added to this bank and on', he: 'נוסף למאגר הזה ופעיל' },
  klArticlesEmpty: {
    en: 'No articles yet. Use Discover or Upload to add content to the Library.',
    he: 'עדיין אין מאמרים. השתמשו בגילוי או בהעלאה כדי להוסיף תוכן לספרייה.',
  },
  klArticlesNoMatch: { en: 'No article matches this search.', he: 'אין מאמר שתואם לחיפוש הזה.' },
  klArticlesLoading: { en: 'Loading articles…', he: 'טוען מאמרים…' },
  klArticlesLoadError: { en: 'Failed to load the Library articles.', he: 'טעינת מאמרי הספרייה נכשלה.' },
  klTotalNone: { en: 'No articles', he: 'אין מאמרים' },
  klTotalOne: { en: '1 article', he: 'מאמר אחד' },
  klTotalMany: { en: '{n} articles', he: '{n} מאמרים' },
  klPageOf: { en: 'Page {page} of {pages}', he: 'עמוד {page} מתוך {pages}' },
  klPrev: { en: 'Previous', he: 'הקודם' },
  klNext: { en: 'Next', he: 'הבא' },
  klDelete: { en: 'Delete', he: 'מחיקה' },
  klDeleteChecking: { en: 'Checking…', he: 'בודק…' },

  // ── Delete (D7: honest about reach) ────────────────────────────────────
  klDeleteTitle: { en: 'Delete this article everywhere?', he: 'למחוק את המאמר הזה מכל מקום?' },
  klDeleteBody: {
    en: '“{name}” will be removed from the Library, with its entries and pictures. It will disappear from every bank listed below, and from every bot that uses those banks. This cannot be undone.',
    he: '“{name}” יוסר מהספרייה, יחד עם הרשומות והתמונות שלו. הוא ייעלם מכל מאגר שמופיע למטה ומכל בוט שמשתמש במאגרים האלה. לא ניתן לבטל את הפעולה.',
  },
  klDeleteOwner: { en: 'Owned by:', he: 'בבעלות:' },
  klDeleteLinked: { en: 'Also used by:', he: 'בשימוש גם אצל:' },
  klDeleteLinkedNone: { en: 'No other bank uses it.', he: 'אף מאגר אחר לא משתמש בו.' },
  klDeleteBots: { en: 'Bots:', he: 'בוטים:' },
  klDeleteNoBots: { en: 'no bots', he: 'אין בוטים' },
  klDeleteBotOne: { en: '1 bot uses this bank', he: 'בוט אחד משתמש במאגר הזה' },
  klDeleteBotMany: { en: '{n} bots use this bank', he: '{n} בוטים משתמשים במאגר הזה' },
  klDeleteUsageFailed: {
    en: 'Could not check where this article is used, so it was not deleted.',
    he: 'לא ניתן היה לבדוק היכן המאמר בשימוש, ולכן הוא לא נמחק.',
  },
  klDeleteFailed: { en: 'The article could not be deleted.', he: 'לא ניתן היה למחוק את המאמר.' },
  klDeletedNone: { en: 'Deleted. No bank had added it.', he: 'נמחק. אף מאגר לא הוסיף אותו.' },
  klDeletedOne: { en: 'Deleted, and removed from 1 bank.', he: 'נמחק והוסר ממאגר אחד.' },
  klDeletedMany: { en: 'Deleted, and removed from {n} banks.', he: 'נמחק והוסר מ-{n} מאגרים.' },

  // ── Library mode of the reused Discovery tab ───────────────────────────
  klDiscoverNote: {
    en: 'Library discovery is not graded: every page found arrives selected. Each bank grades Library articles against its own purpose.',
    he: 'גילוי בספרייה אינו מדורג: כל עמוד שנמצא מגיע מסומן. כל מאגר מדרג את מאמרי הספרייה לפי המטרה שלו.',
  },
  klNotGradedLibrary: { en: 'Not graded — Library', he: 'לא דורג — ספרייה' },
  klNotGradedLibraryTitle: {
    en: 'Pages discovered into the Library are not graded here. Each bank grades them against its own purpose.',
    he: 'עמודים שהתגלו לתוך הספרייה אינם מדורגים כאן. כל מאגר מדרג אותם לפי המטרה שלו.',
  },

  // ── Upload duplicate (409 already_in_library) ──────────────────────────
  klAlreadyInLibrary: {
    en: '“{name}” is already in the Library (in {bank}). It was not uploaded again — add it to a bank from that bank’s “From the Library” tab.',
    he: '“{name}” כבר נמצא בספרייה (ב{bank}). הוא לא הועלה שוב — הוסיפו אותו למאגר מהלשונית “מהספרייה” של אותו מאגר.',
  },
  klAlreadyInLibraryLinked: {
    en: '“{name}” is already in the Library (in {bank}). It was not uploaded again — the existing article was added to this bank.',
    he: '“{name}” כבר נמצא בספרייה (ב{bank}). הוא לא הועלה שוב — המאמר הקיים נוסף למאגר הזה.',
  },
  klAlreadyInLibraryNoBank: {
    en: '“{name}” is already in the Library. It was not uploaded again — add it to a bank from that bank’s “From the Library” tab.',
    he: '“{name}” כבר נמצא בספרייה. הוא לא הועלה שוב — הוסיפו אותו למאגר מהלשונית “מהספרייה” של אותו מאגר.',
  },

  // ── Bank page: "From the Library" tab ──────────────────────────────────
  kbDetailTabLibrary: { en: 'From the Library', he: 'מהספרייה' },
  klBankLoading: { en: 'Loading the Library for this bank…', he: 'טוען את הספרייה עבור המאגר…' },
  klBankListError: { en: 'Failed to load the Library for this bank.', he: 'טעינת הספרייה עבור המאגר נכשלה.' },
  klCountLinked: { en: 'In this bank: {n}', he: 'במאגר הזה: {n}' },
  klCountAvailable: { en: 'Available: {n}', he: 'זמינים: {n}' },
  klCountUngraded: { en: 'Waiting for a grade: {n}', he: 'ממתינים לדירוג: {n}' },
  klRefreshList: { en: 'Grade articles (uses AI credits)', he: 'דירוג מאמרים (צורך קרדיטים של AI)' },
  klRefreshListTitle: {
    en: 'Grades every Library article this bank has not graded yet, against this bank’s purpose, so the list is easy to choose from. Uses AI credits. Grading never adds anything.',
    he: 'מדרג כל מאמר בספרייה שהמאגר עוד לא דירג, לפי מטרת המאגר, כדי שיהיה קל לבחור מהרשימה. צורך קרדיטים של AI. דירוג אף פעם לא מוסיף דבר.',
  },
  klDiscoverGrade: {
    en: 'Grade the results against this bank’s purpose (uses AI credits)',
    he: 'דירוג התוצאות לפי מטרת המאגר (צורך קרדיטים של AI)',
  },
  klDiscoverGradeAll: { en: 'Grade the results ({n}) — uses AI credits', he: 'דירוג התוצאות ({n}) — צורך קרדיטים של AI' },
  klDiscoverUngradedNote: {
    en: 'These results are not graded. Tick the pages to add, or grade them first (uses AI credits). Added pages are stored once in the Knowledge Library and this bank points to them.',
    he: 'התוצאות האלה לא דורגו. סמנו את העמודים להוספה, או דרגו אותם קודם (צורך קרדיטים של AI). עמודים שנוספים נשמרים פעם אחת בספריית הידע והמאגר הזה מצביע עליהם.',
  },
  klRemoveFromBankConfirm: {
    en: 'Remove "{name}" from this bank? The article stays in the Knowledge Library and in every other bank that uses it.',
    he: 'להסיר את "{name}" מהמאגר הזה? המאמר נשאר בספריית הידע ובכל מאגר אחר שמשתמש בו.',
  },
  klRemoveManyFromBankConfirm: {
    en: 'Remove {count} article(s) from this bank? They stay in the Knowledge Library and in every other bank that uses them.',
    he: 'להסיר {count} מאמרים מהמאגר הזה? הם נשארים בספריית הידע ובכל מאגר אחר שמשתמש בהם.',
  },
  klRefreshing: { en: 'Grading… this can take a minute.', he: 'מדרג… זה עשוי לקחת דקה.' },
  klRefreshResult: { en: 'Graded {graded}, {remaining} still waiting.', he: 'דורגו {graded}, {remaining} עדיין ממתינים.' },
  klRefreshFailed: { en: 'Grading could not be run.', he: 'לא ניתן היה להריץ דירוג.' },
  klAutoGradeOn: { en: 'Automatic grading: on', he: 'דירוג אוטומטי: פעיל' },
  klAutoGradeOff: { en: 'Automatic grading: off', he: 'דירוג אוטומטי: כבוי' },
  klAutoGradeTitle: {
    en: 'Set by configuration, not here. When on, new Library articles are graded for every bank that a bot uses. It never adds anything; you still choose.',
    he: 'נקבע בהגדרות, לא כאן. כשהוא פעיל, מאמרים חדשים בספרייה מדורגים לכל מאגר שבוט משתמש בו. הוא אף פעם לא מוסיף דבר; הבחירה נשארת שלכם.',
  },
  klDiscoverFromBank: { en: 'Discover from this bank', he: 'גילוי מהמאגר הזה' },
  klDiscoverUrlLabel: { en: 'Start page', he: 'עמוד התחלה' },
  klDiscoverUrlPlaceholder: { en: 'https://example.com/help', he: 'https://example.com/help' },
  klDiscoverStart: { en: 'Start', he: 'התחלה' },
  klDiscoverStarting: { en: 'Starting…', he: 'מתחיל…' },
  klDiscoverStarted: {
    en: 'Discovery started. Pages land in the Library, ungraded, and this bank grades them first.',
    he: 'הגילוי התחיל. העמודים נכנסים לספרייה ללא דירוג, והמאגר הזה מדרג אותם ראשון.',
  },
  klDiscoverOpenLibrary: { en: 'Open the Library’s Discover tab', he: 'פתיחת לשונית הגילוי של הספרייה' },
  klDiscoverFailed: { en: 'Discovery could not be started.', he: 'לא ניתן היה להתחיל גילוי.' },
  klMinGrade: { en: 'Minimum grade', he: 'דירוג מינימלי' },
  klMinGradeHint: { en: 'Articles without a grade are always shown, last.', he: 'מאמרים ללא דירוג מוצגים תמיד, בסוף.' },
  klAddSelected: { en: 'Add selected ({n})', he: 'הוספת הנבחרים ({n})' },
  klSelectRow: { en: 'Select this article', he: 'בחירת המאמר' },
  klSelectAll: { en: 'Select every shown article that is not in this bank', he: 'בחירת כל המאמרים המוצגים שאינם במאגר' },
  klColGrade: { en: 'Grade', he: 'דירוג' },
  klColInBank: { en: 'In this bank', he: 'במאגר הזה' },
  klNotGraded: { en: 'Not graded', he: 'לא דורג' },
  klGradedByAdmin: { en: 'set by admin', he: 'נקבע ע״י מנהל' },
  klGradedBySystem: { en: 'automatic', he: 'אוטומטי' },
  klGradedByAdminTitle: {
    en: 'An admin set this grade. Automatic grading never overwrites it.',
    he: 'מנהל קבע את הדירוג הזה. דירוג אוטומטי לעולם לא דורס אותו.',
  },
  klWhy: { en: 'Why?', he: 'למה?' },
  klHideWhy: { en: 'Hide', he: 'הסתרה' },
  klNoReason: { en: 'No reason was recorded.', he: 'לא נרשמה סיבה.' },
  klStateOn: { en: 'Added — on', he: 'נוסף — פעיל' },
  klStateOff: { en: 'Added — off', he: 'נוסף — כבוי' },
  klStateNotAdded: { en: 'Not added', he: 'לא נוסף' },
  klStateStoredHere: { en: 'In this bank (stored here)', he: 'במאגר הזה (שמור כאן)' },
  klAdd: { en: 'Add', he: 'הוספה' },
  klRemove: { en: 'Remove', he: 'הסרה' },
  klRemoveTitle: {
    en: 'Removes it from this bank only. The article stays in the Library and in every other bank.',
    he: 'מסיר מהמאגר הזה בלבד. המאמר נשאר בספרייה ובכל מאגר אחר.',
  },
  klTurnOff: { en: 'Turn off', he: 'כיבוי' },
  klTurnOn: { en: 'Turn on', he: 'הפעלה' },
  klSetGrade: { en: 'Set grade', he: 'קביעת דירוג' },
  klGradeInputAria: { en: 'Grade from 0 to 100', he: 'דירוג מ-0 עד 100' },
  klGradeRange: { en: 'Enter a whole number from 0 to 100.', he: 'הזינו מספר שלם בין 0 ל-100.' },
  klSave: { en: 'Save', he: 'שמירה' },
  klCancel: { en: 'Cancel', he: 'ביטול' },
  klActionFailed: { en: 'The change could not be saved.', he: 'לא ניתן היה לשמור את השינוי.' },
  klBankEmpty: { en: 'The Library is empty. Add content from the Library page, or from this bank’s Discovery and Sources.', he: 'הספרייה ריקה. הוסיפו תוכן מדף הספרייה, או מהגילוי והמקורות של המאגר הזה.' },
  klBankFilteredEmpty: { en: 'No article meets the minimum grade.', he: 'אין מאמר שעומד בדירוג המינימלי.' },
  klOpenLibrary: { en: 'Open the Knowledge Library', he: 'פתיחת ספריית הידע' },
  klEditsApplyEverywhere: {
    en: 'An article is stored once. Editing its entries changes it for every bank that uses it.',
    he: 'מאמר נשמר פעם אחת. עריכת הרשומות שלו משנה אותו לכל מאגר שמשתמש בו.',
  },
} satisfies Record<string, { en: string; he: string }>;
